import { Children, useCallback, useRef, useState } from "react";
import Icon from "./Icon";
import { STATE_LAYER } from "./IconButton";
import Menu, { MenuItem } from "./Menu";

/*
  Figma "Pop-up button": 40 tall on fill/secondary, the value in Subheadline Semibold and a
  16px chevron. It opens the ds Menu, as wide as the button (a sheet on phones), with the
  chosen value ticked. `placeholder` is what it reads while nothing is chosen. `onChange`
  gets the chosen value as a string, as a select's would.
*/
export const POP_UP_CLASS = `relative inline-flex h-button-m max-w-full items-center gap-space-8 rounded-radius-md bg-fill-secondary pl-[14px] pr-[10px] text-label-primary ${STATE_LAYER}`;

const MIN_MENU_WIDTH = 160;

const PopUpButton = ({ id, label, value, options, onChange, placeholder, disabled = false, className = "" }) => {
  const [menuWidth, setMenuWidth] = useState(null);
  const buttonRef = useRef(null);
  const open = menuWidth !== null;
  const selected = options.find((option) => String(option.value) === String(value));
  const close = useCallback(() => setMenuWidth(null), []);

  const choose = (next) => {
    onChange(String(next));
    buttonRef.current?.focus();
  };

  return (
    <>
      <button
        id={id}
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() =>
          setMenuWidth(open ? null : Math.max(MIN_MENU_WIDTH, Math.round(buttonRef.current.getBoundingClientRect().width)))
        }
        className={`${POP_UP_CLASS} text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
      >
        <span className={`min-w-0 flex-1 truncate text-subheadline-semibold ${selected ? "" : "text-label-tertiary"}`}>
          {selected?.label ?? placeholder}
        </span>
        <Icon name="chevron-down" size={16} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <Menu open={open} onClose={close} anchorRef={buttonRef} width={menuWidth || MIN_MENU_WIDTH} align="start" label={label} autoFocus>
        <div role="listbox" aria-label={label}>
          {placeholder && (
            <MenuItem role="option" aria-selected={!selected} selected={!selected} onClick={() => choose("")}>
              {placeholder}
            </MenuItem>
          )}
          {options.map((option) => {
            const isSelected = selected === option;
            return (
              <MenuItem
                key={option.value}
                role="option"
                aria-selected={isSelected}
                selected={isSelected}
                disabled={option.disabled}
                detail={isSelected ? <Icon name="check" size={20} className="shrink-0 text-accent-text" /> : null}
                onClick={() => choose(option.value)}
              >
                {option.label}
              </MenuItem>
            );
          })}
        </div>
      </Menu>
    </>
  );
};

/**
 * The same button for lists built in place: its <option>s are the children.
 */
export const PopUpSelect = ({ id, label, value, onChange, placeholder, disabled = false, className = "", children }) => {
  const options = Children.toArray(children)
    .filter((child) => child?.type === "option")
    .map((child) => ({ value: String(child.props.value), label: child.props.children, disabled: child.props.disabled }));
  return (
    <PopUpButton
      id={id}
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
};

export default PopUpButton;
